import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  MessageSquare, 
  Star, 
  Image, 
  FileText, 
  Settings,
  LogOut,
  Mail,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Eye,
  Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Check if admin is authenticated
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/check`, {
        credentials: 'include'
      });
      if (response.ok) {
        setIsAuthenticated(true);
        fetchStats();
      }
    } catch (error) {
      console.error('Auth check error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      if (response.ok) {
        setIsAuthenticated(true);
        fetchStats();
      } else {
        const data = await response.json();
        setLoginError(data.detail || 'Credenciais inválidas');
      }
    } catch (error) {
      setLoginError('Erro de conexão');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/admin/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setIsAuthenticated(false);
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/contacts`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const updateContactStatus = async (contactId, newStatus) => {
    try {
      await fetch(`${API_URL}/api/admin/contacts/${contactId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      fetchContacts();
      fetchStats();
    } catch (error) {
      console.error('Error updating contact:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      if (activeTab === 'contacts') fetchContacts();
      if (activeTab === 'users') fetchUsers();
    }
  }, [activeTab, isAuthenticated]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F7]">
        <Loader2 className="w-8 h-8 animate-spin text-[#C4B5A0]" />
      </div>
    );
  }

  // Login Form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F7] p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-[#D4C4B0]/30 flex items-center justify-center mx-auto mb-4">
              <LayoutDashboard size={32} className="text-[#6B4E37]" />
            </div>
            <h1 className="font-['Cormorant_Garamond'] text-3xl text-[#2C2C2C]">
              Admin Dashboard
            </h1>
            <p className="text-sm text-[#8B7D6B] mt-2">Alma Leve Weddings</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#6B4E37] mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-[#FAF9F7] border border-[#E8E4E0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4B5A0]"
                required
                data-testid="admin-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#6B4E37] mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-[#FAF9F7] border border-[#E8E4E0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4B5A0]"
                required
                data-testid="admin-password"
              />
            </div>

            {loginError && (
              <p className="text-red-500 text-sm text-center">{loginError}</p>
            )}

            <button
              type="submit"
              disabled={loginLoading}
              className="w-full py-3 bg-[#C4B5A0] text-white rounded-lg font-medium hover:bg-[#6B4E37] transition-colors disabled:opacity-50"
              data-testid="admin-login-btn"
            >
              {loginLoading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Entrar'}
            </button>
          </form>

          <button
            onClick={() => navigate('/')}
            className="w-full mt-4 py-2 text-[#8B7D6B] text-sm hover:text-[#6B4E37]"
          >
            Voltar ao site
          </button>
        </div>
      </div>
    );
  }

  // Dashboard Content
  return (
    <div className="min-h-screen bg-[#FAF9F7] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-[#E8E4E0] p-6">
        <div className="mb-8">
          <h1 className="font-['Cormorant_Garamond'] text-xl text-[#2C2C2C]">Alma Leve</h1>
          <p className="text-xs text-[#8B7D6B]">Admin Dashboard</p>
        </div>

        <nav className="space-y-2">
          {[
            { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
            { id: 'contacts', icon: MessageSquare, label: 'Contactos' },
            { id: 'users', icon: Users, label: 'Utilizadores' },
            { id: 'testimonials', icon: Star, label: 'Testemunhos' },
            { id: 'portfolio', icon: Image, label: 'Portfólio' },
            { id: 'blog', icon: FileText, label: 'Blog' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${
                activeTab === item.id
                  ? 'bg-[#C4B5A0]/20 text-[#6B4E37] font-medium'
                  : 'text-[#8B7D6B] hover:bg-[#FAF9F7]'
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon size={18} />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="mt-8 pt-8 border-t border-[#E8E4E0]">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-red-500 hover:bg-red-50 transition-colors"
            data-testid="admin-logout"
          >
            <LogOut size={18} />
            Sair
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && stats && (
          <div>
            <h2 className="font-['Cormorant_Garamond'] text-3xl text-[#2C2C2C] mb-8">Dashboard</h2>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <MessageSquare size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#2C2C2C]">{stats.contacts.total}</p>
                    <p className="text-sm text-[#8B7D6B]">Contactos</p>
                  </div>
                </div>
                {stats.contacts.new > 0 && (
                  <p className="mt-4 text-sm text-green-600">
                    +{stats.contacts.new} novos
                  </p>
                )}
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Users size={24} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#2C2C2C]">{stats.users.total}</p>
                    <p className="text-sm text-[#8B7D6B]">Utilizadores</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                    <TrendingUp size={24} className="text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#2C2C2C]">{stats.simulator.completions}</p>
                    <p className="text-sm text-[#8B7D6B]">Simuladores</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                    <Star size={24} className="text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#2C2C2C]">{stats.testimonials.active}</p>
                    <p className="text-sm text-[#8B7D6B]">Testemunhos</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Contacts */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-medium text-[#2C2C2C] mb-4">Contactos Recentes</h3>
              <div className="space-y-4">
                {stats.recentContacts?.map((contact, index) => (
                  <div key={index} className="flex items-center justify-between py-3 border-b border-[#E8E4E0] last:border-0">
                    <div>
                      <p className="font-medium text-[#2C2C2C]">{contact.name}</p>
                      <p className="text-sm text-[#8B7D6B]">{contact.email}</p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-block px-2 py-1 rounded text-xs ${
                        contact.status === 'new' ? 'bg-green-100 text-green-700' :
                        contact.status === 'contacted' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {contact.status}
                      </span>
                    </div>
                  </div>
                ))}
                {(!stats.recentContacts || stats.recentContacts.length === 0) && (
                  <p className="text-[#8B7D6B] text-sm">Nenhum contacto ainda</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div>
            <h2 className="font-['Cormorant_Garamond'] text-3xl text-[#2C2C2C] mb-8">Contactos</h2>
            
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#FAF9F7]">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#8B7D6B]">Nome</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#8B7D6B]">Email</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#8B7D6B]">Mensagem</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#8B7D6B]">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#8B7D6B]">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((contact) => (
                    <tr key={contact.id} className="border-t border-[#E8E4E0]">
                      <td className="px-6 py-4">
                        <p className="font-medium text-[#2C2C2C]">{contact.name}</p>
                        {contact.phone && <p className="text-sm text-[#8B7D6B]">{contact.phone}</p>}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#6B4E37]">{contact.email}</td>
                      <td className="px-6 py-4 text-sm text-[#8B7D6B] max-w-xs truncate">{contact.message}</td>
                      <td className="px-6 py-4">
                        <select
                          value={contact.status}
                          onChange={(e) => updateContactStatus(contact.id, e.target.value)}
                          className="text-sm border border-[#E8E4E0] rounded px-2 py-1"
                        >
                          <option value="new">Novo</option>
                          <option value="contacted">Contactado</option>
                          <option value="completed">Concluído</option>
                        </select>
                      </td>
                      <td className="px-6 py-4">
                        <a 
                          href={`mailto:${contact.email}`}
                          className="text-[#C4B5A0] hover:text-[#6B4E37]"
                        >
                          <Mail size={18} />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {contacts.length === 0 && (
                <p className="text-center py-8 text-[#8B7D6B]">Nenhum contacto encontrado</p>
              )}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div>
            <h2 className="font-['Cormorant_Garamond'] text-3xl text-[#2C2C2C] mb-8">Utilizadores</h2>
            
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#FAF9F7]">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#8B7D6B]">Nome</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#8B7D6B]">Email</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#8B7D6B]">Autenticação</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#8B7D6B]">Simulador</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#8B7D6B]">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.user_id} className="border-t border-[#E8E4E0]">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {user.picture ? (
                            <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-[#C4B5A0] flex items-center justify-center text-white text-sm">
                              {user.name?.charAt(0)}
                            </div>
                          )}
                          <span className="font-medium text-[#2C2C2C]">{user.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#6B4E37]">{user.email}</td>
                      <td className="px-6 py-4">
                        <span className={`text-xs px-2 py-1 rounded ${
                          user.auth_provider === 'google' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {user.auth_provider}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {user.progress?.simulator_result ? (
                          <CheckCircle size={18} className="text-green-500" />
                        ) : (
                          <XCircle size={18} className="text-gray-300" />
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#8B7D6B]">
                        {new Date(user.created_at).toLocaleDateString('pt-PT')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && (
                <p className="text-center py-8 text-[#8B7D6B]">Nenhum utilizador encontrado</p>
              )}
            </div>
          </div>
        )}

        {/* Placeholder tabs */}
        {['testimonials', 'portfolio', 'blog'].includes(activeTab) && (
          <div>
            <h2 className="font-['Cormorant_Garamond'] text-3xl text-[#2C2C2C] mb-8">
              {activeTab === 'testimonials' && 'Testemunhos'}
              {activeTab === 'portfolio' && 'Portfólio'}
              {activeTab === 'blog' && 'Blog'}
            </h2>
            <div className="bg-white rounded-xl p-12 shadow-sm text-center">
              <Settings size={48} className="text-[#C4B5A0] mx-auto mb-4" />
              <p className="text-[#8B7D6B]">Gestão de {activeTab} em breve disponível</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
